import { searchAutocomplete } from '@_api/search';
import { getClassFunction, htmlEscape } from '@helpers/functions';
import autocomplete from 'autocompleter';

const getClass = getClassFunction('kf-m-search-input');

const element = document.querySelector<HTMLElement>(getClass());

const handleSearch = (el: HTMLElement | null) => {
  if (!el) return;
  const input = el.querySelector<HTMLInputElement>('input[type="text"].kf-m-search-input__input');

  if (!input) return;
  const button = el.querySelector('button');
  const clearButton = el.querySelector<HTMLElement>('.kf-m-search-input__clear-button');
  const optionsContainer = el.querySelector<HTMLDivElement>('.kf-m-search-input__options');
  let autocompleteEl: ReturnType<typeof autocomplete> | undefined = undefined;

  setTimeout(() => {
    input.removeAttribute('autocomplete');
    input.removeAttribute('aria-owns');
    input.removeAttribute('aria-controls');
  }, 1000);

  const handleClick = (e?: MouseEvent | KeyboardEvent) => {
    const peopleBlockEl = document.querySelector('.kf-o-people-search');

    const value = input?.value;

    if (value && peopleBlockEl) {
      if (e && window.tC?.event?.people_search_results) {
        window.tC?.event?.people_search_results(e, {
          search_keywords: value,
          page_path: window.location.pathname,
        });
      }

      peopleBlockEl.dispatchEvent(new CustomEvent('queryChange', { detail: { query: value } }));
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.code !== 'Enter' && e.code !== 'NumpadEnter') return;
    input.blur();
    handleClick(e);
  };

  const handleClearDisplay = () => {
    const value = input.value;

    if (value) {
      clearButton?.classList.add('active');
    } else {
      clearButton?.classList.remove('active');
    }
  };

  const handleClearClick = () => {
    input.value = '';
    if (clearButton) {
      clearButton.classList.remove('active');
    }
  };

  handleClearDisplay();

  const handleEvents = () => {
    button?.addEventListener('click', handleClick);
    input?.addEventListener('keydown', handleKeyDown);
    input?.addEventListener('input', handleClearDisplay);
    input?.addEventListener('focus', handleClearDisplay);
    clearButton?.addEventListener('click', handleClearClick);
    clearButton?.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.code === 'Enter') handleClearClick();
    });
  };

  handleEvents();

  let timeout: ReturnType<typeof setTimeout> | null = null;

  clearButton?.addEventListener('focus', () => {
    timeout = setTimeout(() => {
      autocompleteEl?.fetch();
    }, 300);
  });

  clearButton?.addEventListener('blur', () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  });

  const searchParams = new URLSearchParams(window.location.search);
  const query = searchParams.get('query');

  if (query) {
    input?.setAttribute('value', query);
  }

  interface AutocompleteItem {
    label: string;
    description: string;
    value?: string;
    url?: string;
  }

  const focusOutHandler = (e: Event) => {
    // If focus moves to the autocomplete options, prevent closing
    const relatedTarget = (e as FocusEvent).relatedTarget as HTMLElement | null;

    if (relatedTarget && optionsContainer && optionsContainer.contains(relatedTarget)) {
      // Focus is moving to the options, do nothing
      return;
    }

    if (optionsContainer) {
      optionsContainer.remove();
    }
  };

  input?.closest('.kf-m-search-input__input-wrapper')?.addEventListener('focusout', focusOutHandler);

  let currentText = '';
  let currentSuggestions: AutocompleteItem[] = [];
  const heroWithLanguageCode = input.closest('.kf-o-hero[data-language-code]') as HTMLElement | null;
  const languageCode = heroWithLanguageCode ? heroWithLanguageCode.dataset.languageCode : undefined;

  autocompleteEl = autocomplete<AutocompleteItem>({
    input: input,
    fetch: async function (text, update) {
      handleClearDisplay();
      if (text === currentText && currentSuggestions.length > 0) {
        update([
          ...currentSuggestions,
          {
            label: `View all results for ${htmlEscape(text)}`,
            description: '',
            value: '',
          },
        ]);

        return;
      }
      currentText = text;
      const resp = await searchAutocomplete({ data: { query: text, languageCode } });

      if (resp.success) {
        const suggestions =
          resp?.data?.data?.map((item: AutocompleteItem) => {
            return { label: item.label, description: item.description, value: item.label, url: item.url };
          }) ?? [];

        currentSuggestions = suggestions;
        update([...suggestions, { label: `View all results for ${htmlEscape(text)}`, value: '' }]);
      }
    },
    minLength: 3,
    debounceWaitMs: 300,
    className: 'kf-m-search-input__autocomplete',
    container: optionsContainer ?? undefined,
    disableAutoSelect: true,
    click: (e) => e.fetch(),
    render(item) {
      const div = document.createElement('div');

      if (item.value) {
        const tag = item.url ? 'a' : 'div';
        const divInner = `
          <${tag} href="${item.url ?? ''}" class="kf-m-search-input__autocomplete-item">
            <label class="kf-m-search-input__autocomplete-item__label">${item.label}</label>
            <span class="kf-m-search-input__autocomplete-item__description">${item.description}</span
            </$>
            `;

        div.innerHTML = divInner;
      } else {
        const divInner = `
        <div class="kf-m-search-input__autocomplete-item search">
          <button class='kf-a-button kf-a-button--tertiary kf-a-button--large' ><span>${item.label}</span></button>
        </div>
        `;

        div.innerHTML = divInner;
      }

      return div;
    },
    onSelect(item) {
      if (item.value) {
        if (item.url) {
          window.location.href = item.url;
        } else {
          input.value = item.label;
          handleClick();
        }
      } else {
        handleClick();
      }
    },
  });
};

handleSearch(element);
